if (!customElements.get('product-gallery')) {
  // Track position for slide i (plus an optional drag offset). Slides are
  // spaced by --gallery-slide-gap (component-product-gallery.css), so a
  // fractional gallery width can't round the next photo's first pixel column
  // into view at the edge; each step moves one slide plus one gap.
  const galleryTrackX = (i, dx = 0) =>
    `translateX(calc(${-i * 100}% - ${i} * var(--gallery-slide-gap, 0px) + ${dx}px))`;

  customElements.define('product-gallery', class ProductGallery extends HTMLElement {
    constructor() {
      super()

      this.limit = parseInt(this.dataset.limit)
      this.modal = this.querySelector('.gallery-modal');
      this.galleries = [...this.querySelectorAll('.gallery')]
      this.openModalImages = this.querySelectorAll('.gallery-more:not(.gallery-thumbs-videos)');
      this.openModalVideos = this.querySelectorAll('.gallery-more.gallery-thumbs-videos');
      this.modalTabImages = this.modal.querySelector('[data-target="modal-images"]');
      this.modalTabVideos = this.modal.querySelector('[data-target="modal-videos"]');
      this.modalTriggerImages = this.querySelector('.gallery-modal-triggers [data-target="modal-images"]');
      this.modalTriggerVideos = this.querySelector('.gallery-modal-triggers [data-target="modal-videos"]');
      this.modalImagesContent = this.modal.querySelector('#modal-images');
      this.modalVideosContent = this.modal.querySelector('#modal-videos');
      this.closeModalButton = this.querySelector('.gallery-modal__close');
      this.topRightMoreButton = this.querySelector('.gallery-top-icon .gallery-more');
      this.topRightCloseButton = this.querySelector('.gallery-top-icon .gallery-close');
      this.videoItems = this.querySelectorAll('.gallery-modal__videos-list-item');
      this.playerContainer = this.querySelector('.gallery-modal__videos-player');
      this.states = []
      this.tapThreshold = 1
      this.doubleTapDelay = 300;
    }

    connectedCallback() {
      this.galleries.forEach(gallery => {
        this.initGallery(gallery)
      })

      this.openModalImages?.forEach(trigger => {
        trigger.addEventListener('click', this.openModal.bind(this, 'images'));
      });
      this.openModalVideos?.forEach(trigger => {
        trigger.addEventListener('click', this.openModal.bind(this, 'videos'));
      });
      this.modalTabImages?.addEventListener('click', this.openModal.bind(this, 'images'));
      this.modalTabVideos?.addEventListener('click', this.openModal.bind(this, 'videos'));
      this.modalTriggerImages?.addEventListener('click', this.openModal.bind(this, 'images'));
      this.modalTriggerVideos?.addEventListener('click', this.openModal.bind(this, 'videos'));
      this.closeModalButton.addEventListener('click', this.closeModal.bind(this));

      this.topRightCloseButton?.addEventListener('click', () => {
        const slide = this.querySelector('.gallery-slide.zoomed');
        const img = slide?.querySelector('img');
        if (!img) return;
        slide.isZoomed = false;
        img.style.transform = 'scale(1)';
        img.style.transformOrigin = 'center center';
        slide.classList.remove('zoomed');
        this.topRightMoreButton.classList.remove('hidden');
        this.topRightCloseButton.classList.add('hidden');
      });

      if (this.videoItems.length > 0) {
        this.videoItems.forEach(item => {
          item.addEventListener('click', () => {
            this.loadVideoTemplate(item);
          });
        });
      }

      window.addEventListener('resize', () => {
        this.states.forEach(state => {
          state.track.style.transform = galleryTrackX(state.index)
        })
      });

      subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, ({ data }) => {
        const mediaId = data.target.getAttribute('data-media-id');

        this.setMainImage(mediaId);
      });
    }

    setMainImage(mediaId) {
      if (!mediaId) return;

      this.galleries.forEach(gallery => {
        const thumb = gallery.querySelector(
          `.gallery-thumb[data-media-id="${mediaId}"]`
        );

        if (thumb) {
          thumb.dispatchEvent(new Event('click', { bubbles: true }));
        }
      });
    }

    isDesktop() {
      return window.matchMedia('(min-width: 750px)').matches;
    }

    isModalOpen() {
      return this.modal.classList.contains('show');
    }

    initGallery(gallery) {
      if (gallery.__initialized) return;
      gallery.__initialized = true;

      const getSlides = () => (
        this.isDesktop()
          ? [...gallery.querySelectorAll('.gallery-slide:not(.large-up-hide)')]
          : [...gallery.querySelectorAll('.gallery-slide')]
      );

      const track = gallery.querySelector('.gallery-track')
      const slides = getSlides();
      const thumbs = [...gallery.querySelectorAll('.gallery-thumb')]
      const prev = gallery.querySelectorAll('.gallery-prev')
      const next = gallery.querySelectorAll('.gallery-next')
      const navigation = gallery.querySelector('.gallery-nav-info')
      const state = {
        gallery,
        track,
        slides,
        thumbs,
        index: parseInt(gallery.dataset.initialIndex) || 0,
        pointerDown: false,
        pointerId: null,
        primaryPointerId: null,
        startX: 0,
        deltaX: 0,
        axis: null,
        isZoomed: false,
        lastTap: 0,
        pinch: {
          active: false,
          startDistance: 0,
          startScale: 1,
          currentScale: 1
        }
      }

      this.states.push(state)

      // Exact (fractional) width: offsetWidth rounds, and with the desktop
      // thumbnail column the image is e.g. 884.6px wide, so stepping by 885px
      // let a sliver of the neighbouring image show at the edge.
      const width = () => track.getBoundingClientRect().width
      const update = () => {
        const currentSlides = getSlides();
        // Percent of the track's own width (= one slide), so the position stays
        // exact when the width changes, e.g. when opening the viewer hides the
        // page scrollbar (that briefly clipped the image's left edge).
        track.style.transition = 'transform .35s ease'
        track.style.transform = galleryTrackX(state.index)
        thumbs.forEach(t => t.classList.remove('active'))
        thumbs[state.index]?.classList.add('active')

        navigation.textContent = `${state.index + 1} / ${currentSlides.length}`;

        const activeThumb = thumbs[state.index];

        if (activeThumb) {
          activeThumb.scrollIntoView({
            behavior: 'smooth',
            inline: 'center',
            block: 'nearest'
          });
        }

        getSlides().forEach(slide => {
          slide.isZoomed = false;
          const img = slide.querySelector('img');
          if (!img) return;
          img.style.transform = 'scale(1)';
          img.style.transformOrigin = 'center center';
          slide.classList.remove('zoomed');
          this.topRightMoreButton.classList.remove('hidden');
          this.topRightCloseButton.classList.add('hidden');
        });
      }

      const slideTo = (i) => {
        const currentSlides = getSlides();

        i = Math.max(0, Math.min(i, currentSlides.length - 1))
        if (isNaN(i)) i = 0

        state.index = i

        update()
      }

      prev?.forEach(trigger => {
        trigger.addEventListener('click', () => slideTo(state.index - 1));
      });
      next?.forEach(trigger => {
        trigger?.addEventListener('click', () => slideTo(state.index + 1))
      });

      thumbs.forEach(t => {
        t.addEventListener('click', () => {
          slideTo(Number(t.dataset.index))
        })
      })

      // Vertical desktop strip: drop the bottom fade once there's nothing more
      // to scroll to (or when every thumbnail already fits).
      const thumbStrip = gallery.querySelector('.gallery-thumbs');
      if (thumbStrip && !gallery.closest('.gallery-modal')) {
        const updateFade = () => {
          const atEnd = thumbStrip.scrollTop + thumbStrip.clientHeight >= thumbStrip.scrollHeight - 2;
          thumbStrip.classList.toggle('is-end', atEnd);
          thumbStrip.classList.toggle('is-scrolled', thumbStrip.scrollTop > 2);
        };
        thumbStrip.addEventListener('scroll', updateFade, { passive: true });
        window.addEventListener('resize', updateFade);
        updateFade();
      }

      track.addEventListener('pointerdown', e => {
        state.pointerDown = true
        if (state.primaryPointerId === null) {
          state.primaryPointerId = e.pointerId;
        }
        state.pointerId = e.pointerId;
        state.startX = e.clientX;
        state.startY = e.clientY;
        state.deltaX = 0;
        state.deltaY = 0;
        state.axis = null;

        if (this.isDesktop()) {
          track.setPointerCapture(e.pointerId)
        }

        const slide = getSlides()[state.index];

        if (!this.isDesktop() && this.isModalOpen() && e.pointerType === 'touch') {
          if (!state.touches) state.touches = new Map();

          state.touches.set(e.pointerId, {
            x: e.clientX,
            y: e.clientY
          });

          if (state.touches.size === 2) {
            const [p1, p2] = [...state.touches.values()];

            const dx = p2.x - p1.x;
            const dy = p2.y - p1.y;

            state.pinch.startDistance = Math.hypot(dx, dy);
            state.pinch.startScale = slide?.isZoomed ? 2 : 1;
            state.pinch.currentScale = state.pinch.startScale;
            state.pinch.active = true;
          }
        }

        if (!this.isDesktop() && this.isModalOpen()) {
          const slide = getSlides()[state.index];
          const img = slide?.querySelector('img');
          if (img && slide?.isZoomed) {
            const match = img.style.transform.match(/translate\(([^,]+)px,\s*([^)]+)px\)/);
            state.startPanX = match ? parseFloat(match[1]) : 0;
            state.startPanY = match ? parseFloat(match[2]) : 0;
          } else {
            state.startPanX = 0;
            state.startPanY = 0;
          }
        }
      })

      track.addEventListener('pointermove', e => {
        const slide = getSlides()[state.index];

        if (!this.isDesktop() && this.isModalOpen() && state.pinch?.active) {
          state.touches.set(e.pointerId, {
            x: e.clientX,
            y: e.clientY
          });

          if (state.touches.size < 2) return;

          const [p1, p2] = [...state.touches.values()];

          const dx = p2.x - p1.x;
          const dy = p2.y - p1.y;

          const distance = Math.hypot(dx, dy);

          const slide = getSlides()[state.index];
          const img = slide?.querySelector('img');
          if (!img) return;

          const scaleFactor = distance / state.pinch.startDistance;

          let newScale = state.pinch.startScale * scaleFactor;

          newScale = Math.max(1, Math.min(2, newScale));
          state.pinch.currentScale = newScale;
          img.style.transform = `translate(0px,0px) scale(${newScale})`;
          slide.isZoomed = newScale > 1;

          return;
        }

        if (!this.isDesktop() && e.pointerId !== state.primaryPointerId) return;

        /* ---------------------------
           ZOOM PAN (desktop + mobile)
        ---------------------------- */
        if (slide?.isZoomed) {

          const img = slide.querySelector('img');
          if (!img) return;

          if (this.isDesktop()) {
            const rect = slide.getBoundingClientRect();

            const offsetX = (e.clientX - rect.left) - rect.width / 2;
            const offsetY = (e.clientY - rect.top) - rect.height / 2;

            img.style.transform = `translate(${-offsetX}px, ${-offsetY}px) scale(2)`;
          }
          else {
            if (!state.pointerDown) return;

            const dx = e.clientX - state.startX;
            const dy = e.clientY - state.startY;

            img.style.transform = `translate(${state.startPanX + dx}px, ${state.startPanY + dy}px) scale(2)`;
          }

          return;
        }

        /* ---------------------------
           SLIDER DRAG
        ---------------------------- */

        if (!state.pointerDown) return;

        state.deltaX = e.clientX - state.startX;
        state.deltaY = e.clientY - state.startY;

        if (!state.axis) {
          const absX = Math.abs(state.deltaX);
          const absY = Math.abs(state.deltaY);

          if (absX > 5 || absY > 5) {
            state.axis = absX > absY ? 'x' : 'y';
          }
        }

        if (state.axis === 'x') {
          e.preventDefault();
        }

        if (state.axis !== 'x') return;

        track.style.transition = 'none';
        track.style.transform = galleryTrackX(state.index, state.deltaX);
      }, { passive: false })

      track.addEventListener('touchmove', (e) => {
        if (!state.pointerDown) return;

        const touch = e.touches[0];
        const deltaX = touch.clientX - state.startX;
        const deltaY = touch.clientY - state.startY;

        if (!state.axis) {
          const absX = Math.abs(deltaX);
          const absY = Math.abs(deltaY);

          if (absX > 6 || absY > 6) {
            state.axis = absX > absY ? 'x' : 'y';
          }
        }

        if (state.axis === 'x') {
          e.preventDefault();
        }

      }, { passive: false });

      const endGesture = e => {
        if (e.pointerId === state.primaryPointerId) {
          state.primaryPointerId = null;
        }

        if (state.touches) {
          state.touches.delete(e.pointerId);
        }

        if (state.touches?.size < 2 && state.pinch.active) {
          const slide = getSlides()[state.index];
          const img = slide?.querySelector('img');

          state.pinch.active = false;
          state.pinch.startDistance = 0;

          if (img) {
            if (state.pinch.currentScale > 1.5) {
              img.style.transform = 'translate(0px,0px) scale(2)';
              slide.classList.add('zoomed');
              this.topRightMoreButton.classList.add('hidden');
              this.topRightCloseButton.classList.remove('hidden');
              slide.isZoomed = true;
            } else {
              img.style.transform = 'scale(1)';
              img.style.transformOrigin = 'center center';
              slide.classList.remove('zoomed');
              this.topRightMoreButton.classList.remove('hidden');
              this.topRightCloseButton.classList.add('hidden');
              slide.isZoomed = false;
            }
          }
        }

        if (!state.pointerDown) return

        const absDeltaX = Math.abs(state.deltaX);
        state.pointerDown = false

        if (state.pointerId !== null) {
          try {
            track.releasePointerCapture(state.pointerId)
          } catch {}
        }

        state.pointerId = null

        if (absDeltaX < this.tapThreshold) {
          const now = Date.now();
          const timeSinceLastTap = now - state.lastTap;
          const slide = getSlides()[state.index];
          const img = slide.querySelector('img');
          if (!img) return;

          // Desktop: a click on the main product image opens the full-screen
          // viewer at this image (same as the expand button, and as a tap
          // already does on mobile). Click-to-zoom stays inside the viewer.
          if (this.isDesktop() && !gallery.closest('.gallery-modal') && !this.isModalOpen()) {
            this.openModal('images');
          }
          else if (this.isDesktop()) {
            if (slide.isZoomed) {
              img.style.transform = 'scale(1)';
              img.style.transformOrigin = 'center center';
              slide.classList.remove('zoomed');
              this.topRightMoreButton.classList.remove('hidden');
              this.topRightCloseButton.classList.add('hidden');

              slide.isZoomed = false;
            } else {
              const rect = slide.getBoundingClientRect();
              const offsetX = (e.clientX - rect.left) - rect.width / 2;
              const offsetY = (e.clientY - rect.top) - rect.height / 2;

              img.style.transform = `translate(${-offsetX}px, ${-offsetY}px) scale(2)`;
              slide.classList.add('zoomed');
              this.topRightMoreButton.classList.add('hidden');
              this.topRightCloseButton.classList.remove('hidden');

              slide.isZoomed = true;
            }
          }
          else if (this.isModalOpen() && timeSinceLastTap < this.doubleTapDelay) {
            if (slide.isZoomed) {
              img.style.transform = 'scale(1)';
              img.style.transformOrigin = 'center center';
              slide.classList.remove('zoomed');
              this.topRightMoreButton.classList.remove('hidden');
              this.topRightCloseButton.classList.add('hidden');

              slide.isZoomed = false;
            } else {
              const rect = slide.getBoundingClientRect();
              const x = ((e.clientX - rect.left) / rect.width) * 100;
              const y = ((e.clientY - rect.top) / rect.height) * 100;

              img.style.transformOrigin = `${x}% ${y}%`;
              img.style.transform = 'translate(0px,0px) scale(2)';
              slide.classList.add('zoomed');
              this.topRightMoreButton.classList.add('hidden');
              this.topRightCloseButton.classList.remove('hidden');

              slide.isZoomed = true;
            }

            state.lastTap = 0;
          } else if (!this.isModalOpen()) {
            this.openModal('images');
          }

          state.lastTap = now;
          return
        }

        const threshold = width() * 0.05;

        let change = 0;

        if (state.deltaX < -threshold) {
          change = 1;
        } else if (state.deltaX > threshold) {
          change = -1;
        }

        slideTo(state.index + change);
      }

      track.addEventListener('pointerup', endGesture)
      track.addEventListener('pointercancel', e => {
        if (e.pointerId === state.primaryPointerId) {
          state.primaryPointerId = null;
        }
        state.pointerDown = false
        state.pointerId = null

        if (state.touches) {
          state.touches.delete(e.pointerId);
        }

        state.pinch.active = false;
        state.pinch.startDistance = 0;
      })

      track.addEventListener('lostpointercapture', e => {
        if (state.touches) {
          state.touches.delete(e.pointerId);
        }

        if (!state.touches || state.touches.size === 0) {
          state.pinch.active = false;
          state.pinch.startDistance = 0;
        }

        if (this.isDesktop()) {
          state.pointerDown = false;
          state.pointerId = null;
        } else {
          if (e.pointerId === state.primaryPointerId) {
            state.primaryPointerId = null;
          }
          if (e.pointerId === state.pointerId) {
            state.pointerId = null;
          }
        }
      })

      update()
      setTimeout(() => {
        gallery.classList.add('initialized');
      }, 20);
    }

    loadVideoTemplate(item) {
      const template = item.querySelector('template');

      if (!template) {
        console.warn('No template found');
        return;
      }

      this.querySelector('.gallery-modal__videos-list-item.active')?.classList.remove('active');
      item.classList.add('active');

      const content = template.content.cloneNode(true);
      this.playerContainer.innerHTML = '';

      this.playerContainer.appendChild(content);

      const video = this.playerContainer.querySelector('video');

      if (video) {
        video.play().catch(() => {});
        video.pause();
      }
    }

    openModal(tab) {
      document.body.classList.add('overflow-hidden');
      this.modal.classList.add('show');

      if (this.videoItems.length > 0) {
        this.loadVideoTemplate(this.videoItems[0]);
      }

      const mainState = this.states.find(s => !s.gallery.closest('.gallery-modal'));
      const modalState = this.states.find(s => s.gallery.closest('.gallery-modal'));

      if (mainState && modalState) {
        const targetIndex = mainState.index;
        modalState.index = targetIndex;

        modalState.thumbs.forEach(t => t.classList.remove('active'));
        modalState.thumbs[targetIndex]?.classList.add('active');
        const activeThumb = modalState.thumbs[targetIndex];
        if (activeThumb) {
          activeThumb.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
        }
        const currentSlides = [...modalState.gallery.querySelectorAll('.gallery-slide')];
        const navigation = modalState.gallery.querySelector('.gallery-nav-info');
        if (navigation) {
          navigation.textContent = `${targetIndex + 1} / ${currentSlides.length}`;
        }

        requestAnimationFrame(() => {
          modalState.track.style.transition = 'none';
          modalState.track.style.transform = galleryTrackX(targetIndex);
        });
      }

      if (tab === 'images') {
        this.modalTabVideos?.classList.remove('active');
        this.modalTabImages?.classList.add('active');
        this.modalVideosContent?.classList.remove('show');
        this.modalImagesContent?.classList.add('show');
      } else {
        this.modalTabImages?.classList.remove('active');
        this.modalTabVideos?.classList.add('active');
        this.modalImagesContent?.classList.remove('show');
        this.modalVideosContent?.classList.add('show');
      }
    }

    closeModal() {
      document.body.classList.remove('overflow-hidden');
      this.modal.classList.remove('show');
      this.modalTabVideos?.classList.remove('active');
      this.modalTabImages?.classList.remove('active');
      this.modalVideosContent?.classList.remove('show');
      this.modalImagesContent?.classList.remove('show');
    }
  })

}